import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, Sparkles, Building2, Phone, Mail, MapPin,
  ExternalLink, KanbanSquare, Plus, FileDown, Paperclip, Database, ShieldCheck, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";
import useUiCopy from "@/hooks/useUiCopy";
import { useAuth } from "@/context/AuthContext";
import { fmtCurrencyFull, CF_STAGES, cfStageColor, cfStageLabel, scoreColor, formatFullAddress } from "@/lib/format";
import { ScoreBadge, ScoreRing } from "@/components/ScoreBadge";
import LocationIntelligence from "@/components/maps/LocationIntelligence";
import RentalMarketIntelligence from "@/components/maps/RentalMarketIntelligence";
import AttomIntelligencePanel from "@/components/AttomIntelligencePanel";
import OwnerContactIntelligencePanel from "@/components/OwnerContactIntelligencePanel";
import BusinessOwnerIntelligencePanel from "@/components/BusinessOwnerIntelligencePanel";
import BlotatoPanel from "@/components/BlotatoPanel";
import SocialPostApprovalPanel from "@/components/SocialPostApprovalPanel";
import AddToPipelineButton from "@/components/AddToPipelineButton";
import ScoreBreakdownPanel from "@/components/ScoreBreakdownPanel";
import ManualStageAdvanceControl from "@/components/ManualStageAdvanceControl";
import SequencePreview from "@/components/SequencePreview";
import DocumentTemplatesPanel from "@/components/DocumentTemplatesPanel";
import NegotiationAssistantPanel from "@/components/NegotiationAssistantPanel";
import FollowUpCadencePanel from "@/components/FollowUpCadencePanel";
import AutoOutreachActivityPanel from "@/components/AutoOutreachActivityPanel";
import VerifyEditModal from "@/components/VerifyEditModal";
import { ArchiveOpportunityButton } from "@/components/ArchiveOpportunityButton";

export default function OpportunityDetail() {
  const { oid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const copy = useUiCopy();
  const [opp, setOpp] = useState(null);
  const [activities, setActivities] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescoring, setRescoring] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [reviving, setReviving] = useState(false);
  const [showVerifyEdit, setShowVerifyEdit] = useState(false); // BLOCK 18

  const load = () => {
    Promise.all([
      api.get(`/opportunities/${oid}`),
      api.get("/activities", { params: { opportunity_oid: oid } }),
      api.get(`/ai/document/attachments/${oid}`).catch(() => ({ data: [] })),
      // BLOCK 68 ,  the top-of-page pipeline chip (via AddToPipelineButton)
      // reads THIS subscriber's per-user pipeline stage from
      // /api/pipeline-v2/{oid}/my-status, but the Pipeline Stage dropdown
      // below was reading the *global* opp.cf_stage which is null for any
      // opp whose only pipeline record lives in user_pipeline. That made
      // the badge say "In Your Pipeline · Discovered" while the dropdown
      // silently fell back to "(Not in pipeline)". Merge my_stage in so
      // both surfaces share the same source of truth.
      api.get(`/pipeline-v2/${oid}/my-status`).catch(() => ({ data: {} })),
    ]).then(([o, a, att, ms]) => {
      const myStage = ms?.data?.my_stage || null;
      setOpp({ ...o.data, cf_stage: myStage || o.data?.cf_stage || null });
      setActivities(a.data);
      setAttachments(att.data || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [oid]);

  const rescore = async () => {
    setRescoring(true);
    try {
      const r = await api.post(`/opportunities/${oid}/rescore`);
      setOpp({ ...opp, ai_score: r.data });
      toast.success("AI re-scored");
    } catch {
      toast.error("Re-score failed");
    } finally {
      setRescoring(false);
    }
  };

  const enrichNow = async () => {
    setEnriching(true);
    try {
      const r = await api.post(`/admin/opportunities/${oid}/enrich-now`);
      const b = r.data?.buffer || {};
      toast.success(
        `Ad-hoc enrichment done. Buffer ${b.calls_consumed}/${b.calls_budget} used this month.`
      );
      setOpp(r.data?.opp || opp);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (d && typeof d === "object" && d.message) ||
                  (typeof d === "string" ? d : "Enrich Now failed");
      toast.error(msg);
    } finally {
      setEnriching(false);
    }
  };

  const setStage = async (stage) => {
    if (!stage) return;
    try {
      const r = await api.post(`/pipeline-v2/${oid}/advance`, { stage });
      setOpp(r.data || { ...opp, cf_stage: stage });
      toast.success(`Moved to ${cfStageLabel(stage)}`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "upgrade_required") {
        toast.error(`Upgrade to Pro to advance into ${cfStageLabel(stage)}.`);
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error(detail?.message || "Stage change failed");
      }
    }
  };

  const revive = async () => {
    setReviving(true);
    try {
      const r = await api.post(`/pipeline-v2/${oid}/revive`, { reason: "manual_revive" });
      const rem = r.data?.email_budget_remaining_90d;
      setOpp((cur) => ({ ...(cur || {}), cf_stage: "scored" }));
      toast.success(
        rem === null || rem === undefined
          ? "Revived to Scored."
          : `Revived to Scored. ${rem} of 3 emails remaining for this contact in the next 90 days.`
      );
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "contact_opted_out") {
        toast.error(d.message);
      } else {
        toast.error((typeof d === "string" ? d : d?.message) || "Revive failed");
      }
    } finally {
      setReviving(false);
    }
  };

  const enrich = async () => {
    setEnriching(true);
    try {
      const r = await api.post(`/opportunities/${oid}/enrich-full`);
      setOpp(r.data);
      const results = r.data?.enrichment_results || [];
      const ok = results.filter((x) => x.status === "success").length;
      const skipped = results.filter((x) => x.status === "not_available").length;
      const failed = results.filter((x) => x.status === "failed").length;
      if (ok > 0) {
        toast.success(`Enriched: ${ok} live source(s), ${skipped} unavailable, ${failed} failed`);
      } else {
        toast.warning(`No live sources returned data. ${skipped} unavailable, ${failed} failed.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enrich failed");
    } finally {
      setEnriching(false);
    }
  };

  const addNote = async () => {
    const body = prompt("Note:");
    if (!body) return;
    await api.post("/activities", {
      opportunity_oid: oid,
      type: "note",
      title: body.slice(0, 80),
      body,
    });
    load();
  };

  if (loading || !opp) return <div className="text-slate-500">Loading…</div>;
  // BLOCK 173 ,  a subscriber whose own pipeline references an
  // archived/withdrawn opp gets this friendly card instead of the full
  // detail page (the backend returns this shape instead of 404ing).
  if (opp.available === false) {
    return (
      <div className="space-y-6" data-testid="opp-no-longer-available">
        <Link to="/app/opportunities" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-400">
          <ArrowLeft className="h-4 w-4" /> Back to opportunities
        </Link>
        <div className="ae-card p-10 text-center">
          <h1 className="font-heading text-2xl">{opp.title || "This opportunity"}</h1>
          <p className="text-sm text-slate-400 mt-3">
            No longer available{opp.reason === "withdrawn" ? " ,  withdrawn by the seller." : " ,  removed by the platform."}
          </p>
        </div>
      </div>
    );
  }
  const s = opp.ai_score || {};

  return (
    <div className="space-y-6">
      {showVerifyEdit && (
        <VerifyEditModal
          opp={opp}
          onClose={() => setShowVerifyEdit(false)}
          onSaved={(fresh) => { if (fresh) setOpp(fresh); }}
        />
      )}
      <Link to="/app/opportunities" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-400">
        <ArrowLeft className="h-4 w-4" /> Back to opportunities
      </Link>

      <header className="grid md:grid-cols-3 gap-6 items-start">
        <div className="md:col-span-2">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2 capitalize">{opp.asset_type}</div>
          <h1 className="font-heading text-3xl tracking-tight">{opp.title}</h1>
          <div className="flex items-center gap-2 text-sm text-slate-400 mt-2">
            <MapPin className="h-3.5 w-3.5" />
            <span data-testid="opp-detail-address">{formatFullAddress(opp) || ", "}</span>
          </div>
          {opp.parcel_id && (
            <div className="text-xs text-slate-500 mt-1 font-mono" data-testid="opp-detail-parcel-id">
              Parcel ID: {opp.parcel_id}
              {opp.county ? ` · ${opp.county} County` : ""}
            </div>
          )}
          <div className="flex items-center gap-3 mt-4">
            <div className="font-mono text-3xl font-bold tabular-nums text-emerald-400" data-testid="opp-asking-price">
              {fmtCurrencyFull(opp.asking_price)}
            </div>
            {opp.cf_stage && (
              <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border ${cfStageColor(opp.cf_stage)}`} data-testid="opp-stage">
                {cfStageLabel(opp.cf_stage)}
              </span>
            )}
          </div>
          <div className="mt-4">
            <AddToPipelineButton oid={oid} onAdded={load} />
            {/* BLOCK 18 ,  admin edit-on-verify, only for pending_review */}
            {["admin", "super_admin", "manager"].includes(user?.role) &&
             opp.submission_status === "pending_review" && (
              <button
                onClick={() => setShowVerifyEdit(true)}
                data-testid="opp-verify-edit-btn"
                className="ml-2 inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition"
              >
                <ShieldCheck className="h-3.5 w-3.5" />
                Edit for Verification
              </button>
            )}
            {/* BLOCK 173 ,  admin Archive/Unarchive */}
            {["admin", "super_admin", "manager"].includes(user?.role) && (
              <span className="ml-2 inline-block">
                <ArchiveOpportunityButton
                  oid={oid}
                  archived={opp.admin_archived || opp.withdrawn}
                  onChanged={load}
                  size="lg"
                />
              </span>
            )}
          </div>
        </div>

        <div className="ae-card p-6">
          {/* BLOCK 25: primary displayed score is now v2.1 cf_readiness_score
              (the pipeline-authoritative value). The Claude LLM ai_score below
              stays as a smaller secondary chip when populated. */}
          <ScoreRing
            score={opp.cf_readiness_score ?? 0}
            label="CF Readiness"
            testid="opp-score-value"
          />
          {opp.cf_score_tier?.label && (
            <div className="mt-2 text-[10px] uppercase tracking-widest text-slate-400" data-testid="opp-score-tier">
              {opp.cf_score_tier.label}
            </div>
          )}
          {(s.opportunity_score || 0) > 0 && (
            <div
              className="mt-2 flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-sm border border-white/10 bg-white/[0.02]"
              data-testid="opp-ai-secondary-score"
              title="Claude Sonnet qualitative gut-check ,  narrative opinion, secondary to CF Readiness."
            >
              <span className="text-[10px] uppercase tracking-widest text-slate-500">AI narrative</span>
              <span className="font-mono text-sm text-slate-300 tabular-nums">{s.opportunity_score}</span>
            </div>
          )}
          {opp.verified_direct_seller && (
            <div
              className="mt-3 flex items-center gap-2 px-2.5 py-1.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
              data-testid="opp-verified-direct-seller-badge"
              title="Owner submitted this listing directly and it was reviewed by our team."
            >
              <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
              <span className="text-[10px] uppercase tracking-widest font-medium leading-tight">Verified Direct Seller</span>
            </div>
          )}
          {opp.scoring_stage === "first_pass_fdor" && (
            <div
              className="mt-3 flex items-start gap-2 px-2.5 py-1.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300"
              data-testid="opp-first-pass-scored-badge"
              title={copy.first_pass_tooltip}
            >
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span className="text-[10px] uppercase tracking-widest font-medium leading-tight">
                First-pass Scored<br/>
                <span className="normal-case tracking-normal text-[10px] text-amber-200/80">
                  Not yet fully enriched
                </span>
              </span>
            </div>
          )}
          {opp.compute_feasibility_unknown && (
            <div
              className="mt-3 flex items-start gap-2 px-2.5 py-1.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300"
              data-testid="opp-feasibility-unknown-badge"
              title="Deal Viability signals (equity/mortgage/AVM) are not yet available for this asset type. Score reflects available data only."
            >
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span className="text-[10px] uppercase tracking-widest font-medium leading-tight">
                Feasibility Unknown<br/>
                <span className="normal-case tracking-normal text-[10px] text-amber-200/80">
                  Equity &amp; loan data unavailable
                </span>
              </span>
            </div>
          )}
          <button
            onClick={rescore}
            disabled={rescoring}
            data-testid="opp-rescore-btn"
            className="mt-4 btn-ghost text-xs inline-flex items-center gap-2 w-full justify-center"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${rescoring ? "animate-spin" : ""}`} />
            {rescoring ? "Scoring…" : "Re-run AI score"}
          </button>
          {["admin", "super_admin"].includes(user?.role) && (
            <button
              onClick={enrichNow}
              disabled={enriching}
              data-testid="opp-enrich-now-btn"
              className="mt-2 text-xs inline-flex items-center gap-2 w-full justify-center px-3 py-2 rounded-sm border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 transition disabled:opacity-40"
              title="Admin only: fires 3 ad-hoc property-data calls (preforeclosure + mortgage + home-equity) against this opportunity, drawing from the 800/mo ad-hoc buffer tier. Immediately re-scores."
            >
              <Database className={`h-3.5 w-3.5 ${enriching ? "animate-pulse" : ""}`} />
              {enriching ? "Enriching…" : "Enrich Now (admin)"}
            </button>
          )}
        </div>
      </header>

      {/* AI Reasoning */}
      <div className="ae-card p-6 border-emerald-500/30">
        <div className="flex items-start gap-3">
          <div className="h-8 w-8 rounded-sm bg-emerald-500/15 flex items-center justify-center shrink-0">
            <Sparkles className="h-4 w-4 text-emerald-400" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-emerald-400">AI Analysis</div>
            <p className="text-sm text-slate-200 mt-2 leading-relaxed" data-testid="opp-reasoning">{s.reasoning || "N/A"}</p>
            {s.recommended_structures?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {s.recommended_structures.map((r) => (
                  <span key={r} className="text-[11px] uppercase tracking-wider bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 rounded-sm px-2 py-1">
                    {r.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Score breakdown */}
      <ScoreBreakdownPanel opp={opp} onUpdate={(merged) => setOpp(merged)} />

      <FollowUpCadencePanel opp={opp} />

      <AutoOutreachActivityPanel opp={opp} onUpdate={(fresh) => setOpp((cur) => ({ ...(cur || {}), ...fresh }))} />

      <NegotiationAssistantPanel opp={opp} />

      <SequencePreview opp={opp} />

      <DocumentTemplatesPanel opp={opp} />

      <LocationIntelligence
        opp={opp}
        onCoordsChange={async ({ lat, lng }) => {
          // Cache geocoded coords back to the Opportunity so repeat loads skip Google
          if (opp.lat === lat && opp.lng === lng) return;
          setOpp((cur) => (cur ? { ...cur, lat, lng } : cur));
          try { await api.patch(`/opportunities/${oid}`, { lat, lng }); } catch { /* non-blocking */ }
        }}
      />

      <AttomIntelligencePanel opp={opp} onUpdate={(fresh) => setOpp(fresh)} />

      <OwnerContactIntelligencePanel opp={opp} onUpdate={(fresh) => setOpp(fresh)} />

      <BusinessOwnerIntelligencePanel opp={opp} onUpdate={(fresh) => setOpp(fresh)} />

      <RentalMarketIntelligence
        opp={opp}
        onOppUpdate={(fresh) => setOpp(fresh)}
      />

      <EnrichmentCard opp={opp} enriching={enriching} onEnrich={enrich} />

      <SocialPostApprovalPanel opp={opp} onUpdate={load} />

      <BlotatoPanel opp={opp} />

      <div className="grid md:grid-cols-3 gap-6">
        {/* Details */}
        <div className="ae-card p-6 md:col-span-2 space-y-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Listing Details</div>
          <p className="text-sm text-slate-300 leading-relaxed">{opp.description || "N/A"}</p>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-3 border-t border-white/8 text-xs">
            <KV label="Days on market" v={opp.days_on_market} mono />
            <KV label="Price reductions" v={opp.price_reductions} mono />
            {["admin", "super_admin", "manager"].includes(user?.role) && <KV label="Source" v={opp.source} />}
            {opp.asset_type === "business" && <>
              <KV label="Industry" v={opp.industry} />
              <KV label="Revenue" v={fmtCurrencyFull(opp.revenue)} mono />
              <KV label="Cash flow" v={fmtCurrencyFull(opp.cash_flow)} mono />
              <KV label="EBITDA" v={fmtCurrencyFull(opp.ebitda)} mono />
              <KV label="Employees" v={opp.employees} mono />
            </>}
            {opp.asset_type === "multifamily" && <>
              <KV label="Units" v={opp.units} mono />
              <KV label="NOI" v={fmtCurrencyFull(opp.noi)} mono />
              <KV label="Cap rate" v={opp.cap_rate ? `${opp.cap_rate}%` : "N/A"} mono />
              <KV label="Occupancy" v={opp.occupancy ? `${opp.occupancy}%` : "N/A"} mono />
              <KV label="Year built" v={opp.year_built} mono />
            </>}
            {opp.asset_type === "land" && <>
              <KV label="Acres" v={opp.acreage} mono />
              <KV label="Zoning" v={opp.zoning} />
              <KV label="Utilities" v={opp.utilities} />
              <KV label="County" v={opp.county} />
            </>}
            <KV label="Absentee" v={opp.absentee_owner ? "Yes" : "No"} />
            <KV label="Tax delinquent" v={opp.tax_delinquent ? "Yes" : "No"} />
          </div>

          {opp.listing_url && (
            <a href={opp.listing_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs text-emerald-400 hover:text-emerald-300">
              <ExternalLink className="h-3.5 w-3.5" /> View original listing
            </a>
          )}
        </div>

        {/* Pipeline & Owner */}
        <div className="space-y-4">
          <div className="ae-card p-6 space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-2">
              <KanbanSquare className="h-3.5 w-3.5" /> Pipeline Stage
            </div>
            <select
              data-testid="opp-stage-select"
              value={opp.cf_stage || ""}
              onChange={(e) => setStage(e.target.value)}
              className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2 text-sm"
            >
              <option value="">(Not in pipeline)</option>
              {CF_STAGES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}{s.gated ? " · PRO" : ""}{s.mode === "manual" ? " · Manual" : ""}
                </option>
              ))}
            </select>
            <div className="text-[10px] text-slate-500 leading-relaxed">
              Synced with the Acquisition Pipeline kanban. <span className="text-amber-300/70">PRO</span> stages require a paid tier; <span className="text-violet-300/70">Manual</span> stages must be advanced by you (no auto-transition).
            </div>
            <ManualStageAdvanceControl
              currentStage={opp.cf_stage}
              oid={opp.oid}
              onAdvanced={(fresh) => { if (fresh) setOpp(fresh); load(); }}
            />
            {opp.cf_stage === "dead" && (
              <div className="rounded-sm border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2" data-testid="opp-revive-block">
                <div className="text-[11px] text-slate-400 leading-relaxed">
                  This opportunity is marked Dead. You can revive it back to Scored to restart
                  communication. Contacts who opted out or unsubscribed can never be revived.
                </div>
                <button
                  onClick={revive}
                  disabled={reviving}
                  data-testid="opp-revive-btn"
                  className="w-full text-sm inline-flex items-center justify-center gap-2 px-3 py-2 rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition disabled:opacity-40"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${reviving ? "animate-spin" : ""}`} />
                  {reviving ? "Reviving…" : "Revive to Scored"}
                </button>
              </div>
            )}
            <button
              onClick={() => navigate("/app/assistant", { state: { oid: opp.oid } })}
              data-testid="opp-ai-assistant-btn"
              className="btn-primary w-full text-sm inline-flex items-center justify-center gap-2"
            >
              <Sparkles className="h-4 w-4" /> Analyze with AI
            </button>
          </div>

          <div className="ae-card p-6 space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Owner Contact</div>
            <Contact icon={Building2} v={opp.owner_name || "N/A"} />
            <Contact icon={Phone} v={opp.owner_phone || "N/A"} />
            <Contact icon={Mail} v={opp.owner_email || "N/A"} />
            <Contact icon={MapPin} v={opp.owner_address || "N/A"} />
          </div>
        </div>
      </div>

      {/* Attachments */}
      <div className="ae-card p-6" data-testid="attachments-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-2"><Paperclip className="h-3 w-3" /> Document Attachments</div>
            <div className="font-heading text-lg mt-1">{attachments.length} attached</div>
          </div>
          <Link to="/app/assistant" className="btn-ghost text-sm inline-flex items-center gap-2" data-testid="attach-from-assistant">
            <Plus className="h-3.5 w-3.5" /> Generate & attach from Assistant
          </Link>
        </div>
        {attachments.length === 0 ? (
          <div className="text-sm text-slate-500">No documents attached yet. Use the AI Assistant&apos;s &ldquo;Send via CRM&rdquo; to attach generated LOIs, term sheets, or proposals here.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {attachments.map((a) => {
              const api_url = process.env.REACT_APP_BACKEND_URL;
              const href = `${api_url}/api/ai/document/attachments/${oid}/${a.tid}/download`;
              return (
                <div key={a.tid} className="flex items-center justify-between py-3" data-testid={`attachment-row-${a.tid}`}>
                  <div className="min-w-0">
                    <div className="text-sm text-slate-200 truncate">{a.name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {a.doc_type} · {a.fmt.toUpperCase()} · {(a.size_bytes / 1024).toFixed(0)} KB · {fmtDateTime(a.created_at)}
                    </div>
                    {a.note && <div className="text-[11px] italic text-slate-400 mt-0.5">{a.note}</div>}
                  </div>
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    download={a.filename}
                    className="btn-ghost text-xs inline-flex items-center gap-1.5"
                    data-testid={`attachment-download-${a.tid}`}
                  >
                    <FileDown className="h-3.5 w-3.5" /> Download
                  </a>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Activity timeline */}
      <div className="ae-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Activity Timeline</div>
            <div className="font-heading text-lg mt-1">{activities.length} entries</div>
          </div>
          <button onClick={addNote} data-testid="add-note-btn" className="btn-ghost text-sm inline-flex items-center gap-2">
            <Plus className="h-3.5 w-3.5" /> Add note
          </button>
        </div>
        <div className="space-y-3">
          {activities.length === 0 ? (
            <div className="text-sm text-slate-500">No activity yet. Add a note or change pipeline stage.</div>
          ) : activities.map((a) => (
            <div key={a.aid} className="flex items-start gap-3 text-sm border-l border-emerald-500/30 pl-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-emerald-400">{a.type.replace("_", " ")}</div>
                <div className="text-slate-200">{a.title}</div>
                {a.body && <div className="text-slate-400 text-xs mt-1">{a.body}</div>}
                <div className="text-[10px] text-slate-600 mt-1 font-mono">{fmtDateTime(a.created_at)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ScoreCard({ label, score, testid }) {
  return (
    <div className="ae-card p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-mono text-3xl font-bold tabular-nums mt-2 ${scoreColor(score || 0)}`}>{score || 0}</div>
      <ScoreBadge score={score || 0} size="sm" />
    </div>
  );
}

function KV({ label, v, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-slate-200 ${mono ? "font-mono tabular-nums" : ""}`}>{v ?? "N/A"}</div>
    </div>
  );
}

function Contact({ icon: I, v }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <I className="h-3.5 w-3.5 text-slate-500" />
      <span className="text-slate-200">{v}</span>
    </div>
  );
}

function fmtNum(n) {
  if (n === null || n === undefined) return "N/A";
  try { return Number(n).toLocaleString(); } catch { return String(n); }
}

function fmtMoney(n) {
  if (n === null || n === undefined) return "N/A";
  try { return `$${Number(n).toLocaleString()}`; } catch { return String(n); }
}

function EnrichmentCard({ opp, enriching, onEnrich }) {
  const copy = useUiCopy();
  const e = opp.enrichment || null;
  const results = opp.enrichment_results || [];
  const hasData = e && Object.keys(e).length > 0;
  const successCount = results.filter((r) => r.status === "success").length;
  const score = e?.cf_readiness_score;

  return (
    <div className="ae-card p-6" data-testid="enrichment-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-8 w-8 rounded-sm bg-sky-500/15 flex items-center justify-center shrink-0">
            <Database className="h-4 w-4 text-sky-400" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-sky-400">Public Data Enrichment</div>
            <div className="font-heading text-base mt-1">{copy.enrichment_heading}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Supplemental context. The authoritative CF Readiness Score at the top of this page
              is the pipeline value; the number below is a legacy v1 readiness signal from the
              public-data-only enrichment pipeline and is retained for historical comparison.
            </div>
          </div>
        </div>
        <button
          onClick={onEnrich}
          disabled={enriching}
          data-testid="opp-enrich-btn"
          className="btn-primary text-xs inline-flex items-center gap-2 shrink-0"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${enriching ? "animate-spin" : ""}`} />
          {enriching ? "Enriching..." : hasData ? "Re-run enrichment" : "Enrich now"}
        </button>
      </div>

      {!hasData && (
        <div className="text-sm text-slate-500 mt-4" data-testid="enrichment-empty">
          No enrichment data yet. Click <span className="text-sky-300">Enrich now</span> to run the full pipeline.
        </div>
      )}

      {hasData && (
        <>
          {typeof score === "number" && (
            <div className="mt-5 grid md:grid-cols-4 gap-3" data-testid="cf-score-panel">
              <div className="rounded-sm border border-slate-500/30 bg-slate-500/5 p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-400" title="Legacy v1 readiness ,  retained for historical comparison. Not used by pipeline.">
                  Legacy CF readiness (v1)
                </div>
                <div className="font-mono tabular-nums text-3xl font-bold text-slate-300 mt-1" data-testid="cf-score-value">
                  {score}<span className="text-base text-slate-500">/100</span>
                </div>
                <div className="text-[10px] text-slate-500 mt-1 leading-tight">Superseded by v2.1 score above.</div>
              </div>
              <SubScore label="Ownership & equity" value={e.score_ownership_equity} max={33} testid="cf-sub-ownership" />
              <SubScore label="Motivation signals" value={e.score_motivation_signals} max={33} testid="cf-sub-motivation" />
              <SubScore label="Market & financials" value={e.score_financials} max={34} testid="cf-sub-market" />
              {e.score_rationale && (
                <div className="md:col-span-4 text-xs text-slate-400 italic border-l-2 border-slate-500/40 pl-3" data-testid="cf-rationale">
                  {e.score_rationale}
                </div>
              )}
            </div>
          )}

          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="enrichment-data">
            {results.some((r) => ["US Census Bureau ACS 5-year", "Demographics"].includes(r.source) && r.status === "not_available") && (
              <div className="md:col-span-4 rounded-sm border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-300" data-testid="census-unavailable-hint">
                {copy.census_unavailable}
              </div>
            )}
            {e.acs_median_income != null && <EnrichStat label="Median income" value={fmtMoney(e.acs_median_income)} testid="enrich-income" />}
            {e.acs_median_home_value != null && <EnrichStat label="Median home value" value={fmtMoney(e.acs_median_home_value)} testid="enrich-home-value" />}
            {e.acs_owner_occupied_pct != null && <EnrichStat label="Owner-occupied" value={`${e.acs_owner_occupied_pct}%`} testid="enrich-owner-occupied" />}
            {e.acs_vacancy_rate != null && <EnrichStat label="Vacancy rate" value={`${e.acs_vacancy_rate}%`} testid="enrich-vacancy" />}
            {e.acs_total_housing_units != null && <EnrichStat label="Housing units" value={fmtNum(e.acs_total_housing_units)} testid="enrich-housing" />}
            {e.flood_zone_flag !== undefined && (
              <EnrichStat label="Flood zone" value={e.flood_zone_flag ? `${e.flood_zone || "SFHA"} (high)` : "X (low)"} testid="enrich-flood" />
            )}
            {e.brownfield_flag !== undefined && (
              <EnrichStat label="EPA facilities" value={`${e.epa_regulated_facilities ?? 0}${e.brownfield_flag ? " (elevated)" : ""}`} testid="enrich-brownfield" />
            )}
            {e.sec_edgar_flag !== undefined && (
              <EnrichStat label={copy.sec_filings_label} value={e.sec_edgar_flag ? `${e.sec_edgar_hits ?? 0} hits` : "None"} testid="enrich-sec" />
            )}
            {e.absentee_owner_flag !== undefined && (
              <EnrichStat label="Absentee owner" value={e.absentee_owner_flag ? "Yes" : "No"} testid="enrich-absentee" />
            )}
          </div>

          {results.length > 0 && (
            <div className="mt-5" data-testid="enrichment-sources">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                Sources pulled · {successCount}/{results.length} returned live data
              </div>
              <div className="divide-y divide-white/5 border border-white/8 rounded-sm">
                {results.map((r) => <SourceRow key={r.source} r={r} />)}
              </div>
            </div>
          )}
        </>
      )}

      {opp.last_enriched && (
        <div className="text-[10px] text-slate-600 mt-4 font-mono" data-testid="enrichment-timestamp">
          Last enriched: {fmtDateTime(opp.last_enriched)}
          {opp.enrichment_pull_type && ` · ${opp.enrichment_pull_type}`}
        </div>
      )}
    </div>
  );
}

function SubScore({ label, value, max, testid }) {
  const pct = max ? Math.min(100, ((value || 0) / max) * 100) : 0;
  return (
    <div className="rounded-sm border border-white/8 bg-white/[0.02] p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-mono tabular-nums text-2xl font-bold text-slate-100 mt-1">
        {value ?? 0}<span className="text-xs text-slate-500">/{max}</span>
      </div>
      <div className="h-1 mt-2 bg-white/8 rounded-sm overflow-hidden">
        <div className="h-full bg-emerald-500/70" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const STATUS_PILL = {
  success: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  not_available: "text-slate-400 border-white/10 bg-white/[0.03]",
  failed: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  skipped: "text-slate-500 border-white/10 bg-white/[0.02]",
};

function SourceRow({ r }) {
  const pill = STATUS_PILL[r.status] || STATUS_PILL.failed;
  const tid = (r.source || "").replace(/\W+/g, "-").toLowerCase();
  return (
    <div className="flex items-start justify-between gap-3 px-3 py-2 text-xs" data-testid={`enrich-source-${tid}`}>
      <div className="min-w-0 flex-1">
        <div className="text-slate-200">{r.source}</div>
        {r.error && <div className="text-[11px] text-slate-500 mt-0.5">{r.error}</div>}
      </div>
      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${pill} shrink-0`}>
        {(r.status || "").replace("_", " ")}
      </span>
    </div>
  );
}

function EnrichStat({ label, value, testid }) {
  return (
    <div className="rounded-sm border border-white/8 bg-white/[0.02] p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-mono tabular-nums text-lg font-semibold mt-1 text-slate-100">{value}</div>
    </div>
  );
}
